import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { API_URL } from '../config/api';
import { useAuth } from '../context/AuthContext';

interface YouTubeVideo {
  videoId: string;
  title: string;
  channel: string;
  channelId: string;
  viewCount: number;
  viewCountText: string;
  publishedTime: string;
  duration: string;
  thumbnail: string;
  url: string;
  isShort: boolean;
  description?: string;
  email?: string | null;
}

interface YouTubeSearchResult {
  success: boolean;
  hashtag: string;
  totalVideos: number;
  videos: YouTubeVideo[];
  metadata: {
    pageTitle: string;
    totalVideosText: string;
  };
  error?: string;
}

interface Props {
  showYouTubeModal: boolean;
  setShowYouTubeModal: (show: boolean) => void;
  youtubeSearchResult: YouTubeSearchResult | null;
  setYoutubeSearchResult: (result: YouTubeSearchResult | null) => void;
  searchLogs: string[];
  clearSearchLogs: () => void;
  addSearchLog: (log: string) => void;
  showSearchLogs: boolean;
  setShowSearchLogs: (show: boolean) => void;
  youtubeSearchKeyword: string;
  setYoutubeSearchKeyword: (keyword: string) => void;
  youtubeAddingCreators: Set<string>;
  youtubeAddedCreators: Set<string>;
  youtubeExistingCreators: Set<string>;
  setYoutubeExistingCreators: (creators: Set<string>) => void;
  addYouTubeCreator: (video: YouTubeVideo) => void;
  selectedYouTubeVideos: Set<string>;
  toggleSelectAllYouTubeVideos: () => void;
  toggleYouTubeVideoSelection: (channelId: string) => void;
  addSelectedYouTubeVideos: () => void;
  bulkAddingYouTubeVideos: boolean;
}

const YouTubeSearchModal: React.FC<Props> = ({
  showYouTubeModal,
  setShowYouTubeModal,
  youtubeSearchResult,
  setYoutubeSearchResult,
  searchLogs,
  clearSearchLogs,
  addSearchLog,
  showSearchLogs,
  setShowSearchLogs,
  youtubeSearchKeyword,
  setYoutubeSearchKeyword,
  youtubeAddingCreators,
  youtubeAddedCreators,
  youtubeExistingCreators,
  setYoutubeExistingCreators,
  addYouTubeCreator,
  selectedYouTubeVideos,
  toggleSelectAllYouTubeVideos,
  toggleYouTubeVideoSelection,
  addSelectedYouTubeVideos,
  bulkAddingYouTubeVideos
}) => {
  const { token } = useAuth();
  const [searching, setSearching] = useState(false);
  const [videoTypeFilter, setVideoTypeFilter] = useState<'all' | 'long' | 'shorts'>('all');
  const [emailFilter, setEmailFilter] = useState(false);
  const [minViews, setMinViews] = useState(10000);
  const [sortBy, setSortBy] = useState<'views' | 'date'>('views');
  const [depth, setDepth] = useState(1);

  const handleSearch = async () => {
    if (!youtubeSearchKeyword.trim()) {
      alert('Please enter a keyword');
      return;
    }

    setSearching(true);
    setYoutubeSearchResult(null);
    clearSearchLogs();
    setShowSearchLogs(true);

    try {
      addSearchLog(`🔍 Searching YouTube for keyword: "${youtubeSearchKeyword}"`);
      addSearchLog(`⚙️ Filter - Video Type: ${videoTypeFilter === 'all' ? 'All' : videoTypeFilter === 'shorts' ? 'Shorts Only' : 'Long Videos Only'}`);
      addSearchLog(`⚙️ Filter - Email Only: ${emailFilter ? 'Yes' : 'No'}`);
      addSearchLog(`📊 Depth: ${depth}`);

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 600000); // 10 minutes timeout

      const response = await fetch(`${API_URL}/api/youtube/search`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify({
          keyword: youtubeSearchKeyword,
          searchType: 'keyword',
          onlyShorts: videoTypeFilter === 'shorts',
          emailFilter: emailFilter,
          depth: depth
        }),
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      const data = await response.json();

      if (data.success) {
        const initialCount = data.totalVideos;
        addSearchLog(`✅ Initial results: ${initialCount} videos`);
        addSearchLog(`📊 Total on YouTube: ${data.metadata.totalVideosText}`);

        let currentVideos = data.videos;

        // Step 1: Filter by video type (if not "all")
        if (videoTypeFilter === 'long') {
          const beforeCount = currentVideos.length;
          currentVideos = currentVideos.filter((v: YouTubeVideo) => !v.isShort);
          addSearchLog(`🎯 After video type filter: ${beforeCount} → ${currentVideos.length} (long videos only)`);
        } else if (videoTypeFilter === 'shorts') {
          const beforeCount = currentVideos.length;
          currentVideos = currentVideos.filter((v: YouTubeVideo) => v.isShort);
          addSearchLog(`🎯 After video type filter: ${beforeCount} → ${currentVideos.length} (shorts only)`);
        }

        // Step 2: Filter by minimum views
        if (minViews > 0) {
          const beforeCount = currentVideos.length;
          currentVideos = currentVideos.filter((v: YouTubeVideo) => v.viewCount >= minViews);
          addSearchLog(`🎯 After view count filter: ${beforeCount} → ${currentVideos.length} (${minViews.toLocaleString()}+ views)`);
        }

        // Step 3: Check which channels already exist in DB and filter them out
        addSearchLog(`🔍 Checking for existing channels in database...`);
        const channelIds = currentVideos.map((v: YouTubeVideo) => v.channelId).filter(Boolean);

        try {
          const checkResponse = await fetch(`${API_URL}/api/youtube/accounts/check-existing`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ channelIds })
          });

          const checkData = await checkResponse.json();

          if (checkData.success && checkData.existingChannelIds) {
            const existingSet = new Set<string>(checkData.existingChannelIds as string[]);
            setYoutubeExistingCreators(existingSet);

            const beforeCount = currentVideos.length;
            // Filter out existing channels
            currentVideos = currentVideos.filter((v: YouTubeVideo) => !existingSet.has(v.channelId));

            if (existingSet.size > 0) {
              addSearchLog(`🗄️  After existing filter: ${beforeCount} → ${currentVideos.length} (removed ${existingSet.size} already in DB)`);
            } else {
              addSearchLog(`✓ No existing channels found - all are new!`);
            }
          }
        } catch (checkError) {
          console.error('Error checking existing channels:', checkError);
          addSearchLog(`⚠️ Could not check existing channels`);
        }

        // Step 4: Filter by email (if enabled)
        if (emailFilter) {
          const beforeCount = currentVideos.length;
          currentVideos = currentVideos.filter((v: YouTubeVideo) => v.email);
          addSearchLog(`📧 After email filter: ${beforeCount} → ${currentVideos.length} (with email only)`);
        }

        // Final count
        addSearchLog(`✅ Final results: ${currentVideos.length} channels`);

        // Sort videos
        if (sortBy === 'views') {
          currentVideos.sort((a: YouTubeVideo, b: YouTubeVideo) => b.viewCount - a.viewCount);
        }

        // Update data with filtered videos
        data.videos = currentVideos;
        data.totalVideos = currentVideos.length;

        setYoutubeSearchResult(data);
      } else {
        addSearchLog(`❌ Search failed: ${data.error || 'Unknown error'}`);
        alert(`Search failed: ${data.error || 'Unknown error'}`);
      }
    } catch (error: any) {
      addSearchLog(`❌ Error: ${error.message}`);
      alert(`Error searching YouTube: ${error.message}`);
    } finally {
      setSearching(false);
    }
  };

  const formatViews = (views: number): string => {
    if (views >= 1000000) {
      return `${(views / 1000000).toFixed(1)}M`;
    }
    if (views >= 1000) {
      return `${(views / 1000).toFixed(1)}K`;
    }
    return views.toString();
  };

  if (!showYouTubeModal) return null;

  return createPortal(
    <div
      className="modal-overlay"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0,0,0,0.7)',
        zIndex: 9998,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          setShowYouTubeModal(false);
        }
      }}
    >
      <div
        className="modal-content"
        style={{
          background: '#1a1a1a',
          padding: '30px',
          borderRadius: '12px',
          width: '90%',
          maxWidth: '1400px',
          maxHeight: '85vh',
          display: 'flex',
          flexDirection: 'column',
          color: 'white',
          position: 'relative'
        }}
      >
        {/* Modal Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '20px',
          borderBottom: '1px solid #333',
          paddingBottom: '15px'
        }}>
          <h2 style={{
            margin: 0,
            fontSize: '24px',
            display: 'flex',
            alignItems: 'center',
            gap: '10px'
          }}>
            <span style={{ fontSize: '28px' }}>📹</span>
            YouTube Video Search
          </h2>
          <button
            onClick={() => setShowYouTubeModal(false)}
            style={{
              background: 'none',
              border: 'none',
              color: '#999',
              fontSize: '28px',
              cursor: 'pointer',
              padding: '0',
              width: '32px',
              height: '32px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            ×
          </button>
        </div>

        {/* Search Controls */}
        <div style={{
          display: 'flex',
          gap: '15px',
          marginBottom: '20px',
          alignItems: 'flex-end'
        }}>
          <div style={{ flex: 1 }}>
            <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px' }}>
              Keyword
            </label>
            <input
              type="text"
              value={youtubeSearchKeyword}
              onChange={(e) => setYoutubeSearchKeyword(e.target.value)}
              placeholder="Enter keyword (e.g., AI automation)"
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #333',
                borderRadius: '6px',
                background: '#2a2a2a',
                color: 'white',
                fontSize: '14px'
              }}
              onKeyPress={(e) => {
                if (e.key === 'Enter' && !searching) {
                  handleSearch();
                }
              }}
            />
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px' }}>
              Video Type
            </label>
            <select
              value={videoTypeFilter}
              onChange={(e) => setVideoTypeFilter(e.target.value as 'all' | 'long' | 'shorts')}
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #333',
                borderRadius: '6px',
                background: '#2a2a2a',
                color: 'white',
                fontSize: '14px',
                cursor: 'pointer'
              }}
            >
              <option value="all">All</option>
              <option value="long">Long Videos only</option>
              <option value="shorts">YouTube Shorts only</option>
            </select>
          </div>

          <div>
            <label style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              cursor: 'pointer',
              fontSize: '14px'
            }}>
              <input
                type="checkbox"
                checked={emailFilter}
                onChange={(e) => setEmailFilter(e.target.checked)}
                style={{ width: '18px', height: '18px' }}
              />
              <span>Only creators with emails</span>
            </label>
          </div>

          <div>
            <label style={{
              display: 'block',
              marginBottom: '5px',
              fontSize: '14px',
              position: 'relative'
            }}>
              Depth
              <span
                style={{
                  marginLeft: '8px',
                  color: '#999',
                  fontSize: '12px',
                  cursor: 'help',
                  position: 'relative',
                  display: 'inline-block'
                }}
                title="1 depth ≈ 36 videos. Higher depth fetches more results (depth × 36)"
              >
                ⓘ
              </span>
            </label>
            <input
              type="number"
              value={depth}
              onChange={(e) => {
                const value = parseInt(e.target.value);
                if (value > 0 && value <= 100) {
                  setDepth(value);
                }
              }}
              min={1}
              max={100}
              placeholder="1-100"
              title={`Current: ${depth} depth ≈ ${depth * 36} videos`}
              style={{
                width: '100px',
                padding: '10px',
                border: '1px solid #333',
                borderRadius: '6px',
                background: '#2a2a2a',
                color: 'white',
                fontSize: '14px',
                cursor: 'pointer'
              }}
            />
            <div style={{
              fontSize: '11px',
              color: '#666',
              marginTop: '4px'
            }}>
              ≈ {depth * 36} videos
            </div>
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px' }}>
              Min Views
            </label>
            <input
              type="number"
              value={minViews}
              onChange={(e) => setMinViews(parseInt(e.target.value) || 0)}
              placeholder="Min views"
              style={{
                width: '120px',
                padding: '10px',
                border: '1px solid #333',
                borderRadius: '6px',
                background: '#2a2a2a',
                color: 'white',
                fontSize: '14px'
              }}
            />
          </div>

          <button
            onClick={handleSearch}
            disabled={searching || !youtubeSearchKeyword}
            style={{
              padding: '10px 24px',
              background: searching
                ? '#666'
                : 'linear-gradient(135deg, #FF0000 0%, #CC0000 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: searching ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              fontWeight: 'bold',
              opacity: searching || !youtubeSearchKeyword ? 0.7 : 1
            }}
          >
            {searching ? 'Searching...' : 'Search'}
          </button>
        </div>

        {/* Search Logs */}
        {showSearchLogs && searchLogs.length > 0 && (
          <div style={{
            background: '#0a0a0a',
            border: '1px solid #333',
            borderRadius: '6px',
            padding: '10px',
            marginBottom: '20px',
            maxHeight: '120px',
            overflowY: 'auto',
            fontSize: '12px',
            fontFamily: 'monospace'
          }}>
            {searchLogs.map((log, index) => (
              <div key={index} style={{ marginBottom: '4px', color: '#0f0' }}>
                {log}
              </div>
            ))}
          </div>
        )}

        {/* Results */}
        <div style={{
          flex: 1,
          overflowY: 'auto',
          background: '#0a0a0a',
          borderRadius: '8px',
          padding: '15px'
        }}>
          {youtubeSearchResult ? (
            <div>
              <div style={{
                marginBottom: '15px',
                paddingBottom: '10px',
                borderBottom: '1px solid #333'
              }}>
                <h3 style={{ margin: '0 0 5px 0', fontSize: '18px' }}>
                  Results for #{youtubeSearchResult.hashtag}
                </h3>
                <p style={{ margin: 0, color: '#999', fontSize: '14px' }}>
                  Total on YouTube: {youtubeSearchResult.metadata.totalVideosText} •
                  Showing {youtubeSearchResult.videos.length} filtered {youtubeSearchResult.videos.length === 1 ? 'video' : 'videos'}
                </p>
              </div>

              {/* Select All Checkbox */}
              {youtubeSearchResult.videos.length > 0 && (
                <div style={{ marginBottom: '15px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}>
                    <input
                      type="checkbox"
                      checked={selectedYouTubeVideos.size > 0 &&
                        youtubeSearchResult.videos
                          .filter((video: YouTubeVideo) => {
                            return !youtubeAddingCreators.has(video.channelId) &&
                                   !youtubeAddedCreators.has(video.channelId) &&
                                   !youtubeExistingCreators.has(video.channelId);
                          })
                          .every((video: YouTubeVideo) => selectedYouTubeVideos.has(video.channelId))}
                      onChange={toggleSelectAllYouTubeVideos}
                      style={{ width: '18px', height: '18px' }}
                    />
                    <span>Select All</span>
                  </label>

                  {selectedYouTubeVideos.size > 0 && (
                    <button
                      onClick={addSelectedYouTubeVideos}
                      disabled={bulkAddingYouTubeVideos}
                      style={{
                        padding: '8px 16px',
                        background: bulkAddingYouTubeVideos
                          ? '#666'
                          : 'linear-gradient(135deg, #FF0000 0%, #CC0000 100%)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: bulkAddingYouTubeVideos ? 'not-allowed' : 'pointer',
                        fontSize: '14px',
                        fontWeight: 'bold',
                        opacity: bulkAddingYouTubeVideos ? 0.7 : 1
                      }}
                    >
                      {bulkAddingYouTubeVideos
                        ? 'Adding...'
                        : `Add Selected (${selectedYouTubeVideos.size})`}
                    </button>
                  )}
                </div>
              )}

              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                gap: '15px'
              }}>
                {youtubeSearchResult.videos.map((video) => (
                  <div
                    key={video.videoId}
                    style={{
                      background: '#1a1a1a',
                      borderRadius: '8px',
                      overflow: 'hidden',
                      border: '1px solid #333',
                      transition: 'transform 0.2s, box-shadow 0.2s',
                      cursor: 'pointer'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(255,0,0,0.1)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                    onClick={() => window.open(video.url, '_blank')}
                  >
                    {/* Thumbnail */}
                    <div style={{
                      position: 'relative',
                      paddingBottom: '56.25%',
                      background: '#000'
                    }}>
                      <img
                        src={video.thumbnail}
                        alt={video.title}
                        style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover'
                        }}
                      />
                      {/* Selection Checkbox */}
                      {!youtubeAddingCreators.has(video.channelId) &&
                       !youtubeAddedCreators.has(video.channelId) &&
                       !youtubeExistingCreators.has(video.channelId) && (
                        <div
                          style={{
                            position: 'absolute',
                            top: '8px',
                            right: '8px',
                            zIndex: 10
                          }}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            toggleYouTubeVideoSelection(video.channelId);
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={selectedYouTubeVideos.has(video.channelId)}
                            onChange={() => {}}
                            style={{
                              width: '20px',
                              height: '20px',
                              cursor: 'pointer'
                            }}
                          />
                        </div>
                      )}
                      {/* Duration */}
                      <div style={{
                        position: 'absolute',
                        bottom: '8px',
                        right: '8px',
                        background: 'rgba(0,0,0,0.8)',
                        padding: '2px 6px',
                        borderRadius: '4px',
                        fontSize: '12px',
                        fontWeight: 'bold'
                      }}>
                        {video.duration}
                      </div>
                      {/* Shorts badge */}
                      {video.isShort && (
                        <div style={{
                          position: 'absolute',
                          top: '8px',
                          left: '8px',
                          background: '#ff0000',
                          padding: '2px 8px',
                          borderRadius: '4px',
                          fontSize: '11px',
                          fontWeight: 'bold',
                          textTransform: 'uppercase'
                        }}>
                          Shorts
                        </div>
                      )}
                    </div>

                    {/* Video Info */}
                    <div style={{ padding: '12px' }}>
                      <h4 style={{
                        margin: '0 0 8px 0',
                        fontSize: '14px',
                        lineHeight: '1.3',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}>
                        {video.title}
                      </h4>
                      <p style={{
                        margin: '0 0 4px 0',
                        color: '#999',
                        fontSize: '13px'
                      }}>
                        {video.channel}
                      </p>
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        fontSize: '12px',
                        color: '#666',
                        marginBottom: '8px'
                      }}>
                        <span>{formatViews(video.viewCount)} views</span>
                        <span>{video.publishedTime}</span>
                      </div>

                      {/* Email Display */}
                      {video.email && (
                        <div style={{
                          backgroundColor: 'rgba(34, 197, 94, 0.2)',
                          color: '#22c55e',
                          border: '1px solid #22c55e',
                          borderRadius: '4px',
                          padding: '4px 6px',
                          fontSize: '10px',
                          fontWeight: 'bold',
                          textAlign: 'center',
                          marginBottom: '8px',
                          wordBreak: 'break-all'
                        }}>
                          📧 {video.email}
                        </div>
                      )}

                      {/* Add Button */}
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          addYouTubeCreator(video);
                        }}
                        disabled={
                          youtubeAddingCreators.has(video.channelId) ||
                          youtubeAddedCreators.has(video.channelId) ||
                          youtubeExistingCreators.has(video.channelId)
                        }
                        style={{
                          width: '100%',
                          padding: '8px',
                          backgroundColor: youtubeExistingCreators.has(video.channelId) ? '#51cf66' :
                                         youtubeAddedCreators.has(video.channelId) ? '#868e96' :
                                         youtubeAddingCreators.has(video.channelId) ? '#666' :
                                         '#FF0000',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          fontSize: '13px',
                          fontWeight: 'bold',
                          cursor: youtubeAddingCreators.has(video.channelId) ||
                                 youtubeAddedCreators.has(video.channelId) ||
                                 youtubeExistingCreators.has(video.channelId) ? 'not-allowed' : 'pointer',
                          opacity: youtubeAddingCreators.has(video.channelId) ||
                                  youtubeAddedCreators.has(video.channelId) ||
                                  youtubeExistingCreators.has(video.channelId) ? 0.7 : 1,
                          transition: 'all 0.2s'
                        }}
                        onMouseEnter={(e) => {
                          if (!youtubeAddingCreators.has(video.channelId) &&
                              !youtubeAddedCreators.has(video.channelId) &&
                              !youtubeExistingCreators.has(video.channelId)) {
                            e.currentTarget.style.opacity = '0.9';
                          }
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.opacity = '1';
                        }}
                      >
                        {youtubeExistingCreators.has(video.channelId) ? '✓ In DB' :
                         youtubeAddedCreators.has(video.channelId) ? '✓ Added' :
                         youtubeAddingCreators.has(video.channelId) ? '⏳ Adding...' :
                         '➕ Add'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div style={{
              textAlign: 'center',
              padding: '40px',
              color: '#666'
            }}>
              {searching ? 'Searching...' : 'Enter a keyword and click search to find YouTube videos'}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default YouTubeSearchModal;